// Custom marker pins (data-URI SVGs) + Mapbox image registration.
// Each category has its OWN color so pins are distinguishable at a glance;
// the glyph is drawn in white on the colored teardrop.
import { DEFAULT_POI_ICON, POI_CATEGORIES } from './poiCategories';

// Internal seam: call through exports so jest.spyOn intercepts correctly.

import * as self from './poiPinIcons';

// One distinct color per category (+ a petrol fallback). Chosen to be maximally
// separable on the map's light/green basemap.
export const POI_COLORS = {
  sights: '#8E44AD', // purple — Sehenswürdigkeiten / Kultur
  nature: '#2E7D32', // green — Natur & Outdoor
  supply: '#1565C0', // blue — Versorgung
  fuel: '#D32F2F', // red — Tankstellen
  cityFood: '#EF6C00', // orange — Essen & Stadt-Services
  default: '#0E3B43', // petrol fallback
};

// Teardrop pin (40x52): colored body with a thin white outline + white glyph.
// Glyphs are stroke-based so they read on any fill.
const pin = (glyph, color) =>
  `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="52" viewBox="0 0 40 52">` +
  `<path d="M20 1C9.5 1 1 9.5 1 20c0 13.5 19 31 19 31s19-17.5 19-31C39 9.5 30.5 1 20 1z" ` +
  `fill="${color}" stroke="#FFFFFF" stroke-width="2"/>` +
  `<g transform="translate(20 20)" fill="none" stroke="#FFFFFF" stroke-width="2.5" ` +
  `stroke-linecap="round" stroke-linejoin="round">${glyph}</g></svg>`;

// Glyph paths, centered at the origin (range ~±7). Reused both inside the map
// pin (translate(20 20)) and standalone in the native control buttons
// (viewBox centered on 0,0), recolored there via currentColor. Stroke-based so
// they render in white on the pins and in the category color in the buttons.
export const POI_GLYPHS = {
  // classical building (pediment + columns) — reads as Kultur / Sehenswürdigkeit
  sights:
    '<path d="M-7-1 0-7 7-1"/><path d="M-5 0.5v4.5"/><path d="M0 0.5v4.5"/>' +
    '<path d="M5 0.5v4.5"/><path d="M-7 6.5h14"/>',
  nature: '<path d="M0-7l5 8h-3l3 5h-10l3-5h-3z"/><path d="M0 6v3"/>', // tree
  // shopping cart — reads as Versorgung / Einkauf
  supply:
    '<path d="M-7-3h2l1.6 8h7l1.4-6h-10"/>' +
    '<path d="M-4 7m-1 0a1 1 0 102 0a1 1 0 10-2 0"/>' +
    '<path d="M3 7m-1 0a1 1 0 102 0a1 1 0 10-2 0"/>',
  cityFood:
    '<path d="M-3-6v12"/><path d="M-5-6v4a2 2 0 004 0v-4"/>' +
    '<path d="M4-6c-2 0-2 4-2 6h2z"/><path d="M4 0v6"/>', // fork+knife
  // fuel pump: body on the left, hose + nozzle on the right — reads as Tankstelle
  fuel:
    '<path d="M-6-7h7v14h-7z"/>' + // pump body
    '<path d="M-6-2h7"/>' + // display window line
    '<path d="M1-5h2.5a1.5 1.5 0 011.5 1.5v6a1.5 1.5 0 003 0V-3l-2-2"/>', // hose + nozzle
  default: '<circle r="3.5"/>', // dot/ring
};

// id -> category color (with fallback)
const colorFor = (id) => POI_COLORS[id] ?? POI_COLORS.default;

const toDataUri = (svg) => `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;

// id -> data-URI for the marker pins
export const POI_PIN_DATA_URIS = POI_CATEGORIES.reduce((acc, c) => {
  acc[c.id] = toDataUri(pin(POI_GLYPHS[c.id] ?? POI_GLYPHS.default, colorFor(c.id)));
  return acc;
}, {});

// mapbox iconId -> data-URI for addImage (incl. the default pin)
export const POI_ICON_IMAGES = POI_CATEGORIES.reduce(
  (acc, c) => {
    acc[c.iconId] = toDataUri(pin(POI_GLYPHS[c.id] ?? POI_GLYPHS.default, colorFor(c.id)));
    return acc;
  },
  { [DEFAULT_POI_ICON]: toDataUri(pin(POI_GLYPHS.default, POI_COLORS.default)) }
);

// Exported so tests can spy on this via the module namespace.
export const loadPoiPinImage = (dataUri) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.decoding = 'async';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = dataUri;
  });

export const ensurePoiIcons = async (map) => {
  if (!map) return;
  await Promise.all(
    Object.entries(POI_ICON_IMAGES).map(async ([iconId, uri]) => {
      if (map.hasImage(iconId)) return;
      try {
        const img = await self.loadPoiPinImage(uri);
        if (!map.hasImage(iconId)) map.addImage(iconId, img, { pixelRatio: 2 });
      } catch {
        /* missing icon -> DEFAULT_POI_ICON fallback in the icon-image expression */
      }
    })
  );
};
